import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { apiRequest } from '../api/client.js';
import { isBrowserSourceRoute } from '../browser-source.js';
import type { ChatPreferences, EmoteMap } from '../api/types.js';
import { DEFAULT_CHAT_PREFERENCES } from '../api/types.js';

/**
 * One person's Console preferences (ARCHITECTURE.md §22, §65).
 *
 * Held in context rather than fetched per component: the chat feed renders
 * hundreds of rows and every one reads these, so a hook that fetched would
 * issue a request per row.
 *
 * The defaults are used until the real values arrive, and are used again if the
 * request fails. A preferences outage must degrade to Connaro's normal
 * appearance, never to a blank feed — these values reach the DOM on every row,
 * and the Console has no error boundary below this point that could soften an
 * undefined.
 */

type PreferencesValue = {
  preferences: ChatPreferences;
  /**
   * name → emote id, empty until the provider is enabled AND the fetch lands.
   *
   * Empty is the working default: an empty map makes `segmentEmotes` a no-op,
   * so a feed renders correctly while this is in flight, when 7TV is down, and
   * for every workspace that never turns it on.
   */
  emotes: EmoteMap;
  /** Persists a partial change and applies it locally on success. */
  update: (patch: Partial<ChatPreferences>) => Promise<void>;
  /** False until the first load settles, so a control can avoid flicker. */
  loaded: boolean;
};

const PreferencesContext = createContext<PreferencesValue>({
  preferences: DEFAULT_CHAT_PREFERENCES,
  emotes: {},
  update: () => Promise.resolve(),
  loaded: false,
});

export function PreferencesProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [preferences, setPreferences] = useState<ChatPreferences>(DEFAULT_CHAT_PREFERENCES);
  const [loaded, setLoaded] = useState(false);
  const [emotes, setEmotes] = useState<EmoteMap>({});

  useEffect(() => {
    /*
     * An AbortController rather than a `cancelled` flag: the flag reads as
     * always-false to the linter's flow analysis inside the async closure, and
     * this also stops the request itself rather than merely ignoring its
     * result when the Console navigates away mid-flight.
     */
    /*
     * A browser source has no preferences to read (browser-source.ts). Marking
     * it loaded with the defaults is the honest state: nothing is pending.
     */
    if (isBrowserSourceRoute()) {
      setLoaded(true);
      return undefined;
    }

    const controller = new AbortController();

    void (async () => {
      try {
        const data = await apiRequest<{ preferences: ChatPreferences }>('/preferences', {
          signal: controller.signal,
        });
        setPreferences(data.preferences);
      } catch {
        // Deliberately silent: an anonymous visitor on /login gets a 401 here,
        // and that is not a fault worth reporting to them. The defaults stand.
      } finally {
        if (!controller.signal.aborted) setLoaded(true);
      }
    })();

    return () => {
      controller.abort();
    };
  }, []);

  /*
   * The theme is an attribute on the ROOT element, not a class on a wrapper.
   *
   * `tokens.css` defines each theme as `[data-theme='…']`, and the OBS dock
   * and the auth pages render outside any Console wrapper — an attribute on
   * <html> reaches all of them, and reaches `color-scheme` too, which decides
   * how the browser draws scrollbars and form controls.
   */
  useEffect(() => {
    document.documentElement.dataset['theme'] = preferences.theme;
  }, [preferences.theme]);

  /*
   * Fetched only when the preference is on, and cleared when it goes off — so
   * a workspace that has not enabled 7TV never causes a request to it, and
   * turning it off takes effect on the next render rather than at reload.
   */
  const enabled = [
    preferences.sevenTvEmotes ? 'seventv' : undefined,
    preferences.bttvEmotes ? 'bttv' : undefined,
    preferences.ffzEmotes ? 'ffz' : undefined,
  ]
    .filter((value): value is string => value !== undefined)
    .join(',');

  useEffect(() => {
    if (enabled === '') {
      setEmotes({});
      return undefined;
    }

    const controller = new AbortController();

    void (async () => {
      try {
        /*
         * The enabled providers are named in the REQUEST, so a workspace that
         * turned on only 7TV never causes a request to BetterTTV or FFZ. The
         * server would otherwise have to guess, and guessing would mean
         * fetching from services the customer did not agree to.
         */
        const data = await apiRequest<{ emotes: EmoteMap }>(`/emotes?providers=${enabled}`, {
          signal: controller.signal,
        });
        setEmotes(data.emotes);
      } catch {
        // Emotes are decoration on a feed that works without them.
      }
    })();

    return () => {
      controller.abort();
    };
  }, [enabled]);

  const update = useCallback(async (patch: Partial<ChatPreferences>) => {
    /*
     * Applied from the SERVER's response, not optimistically from the patch.
     * The server clamps and validates, so echoing the request would show a
     * value that was never stored — a font size of 400 would appear to work
     * until the next reload.
     */
    const data = await apiRequest<{ preferences: ChatPreferences }>('/preferences', {
      method: 'PATCH',
      body: patch,
    });
    setPreferences(data.preferences);
  }, []);

  const value = useMemo(
    () => ({ preferences, emotes, update, loaded }),
    [preferences, emotes, update, loaded],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesValue {
  return useContext(PreferencesContext);
}
